import { computed, onScopeDispose, ref, shallowRef, toValue, watch } from 'vue'
import type { MaybeRefOrGetter, Ref, ShallowRef } from 'vue'

import type { RelabelPreview } from '../models/credit.types'
import { useCreditStore } from '../stores/creditStore'

import { useWorkspace } from '@/features/account'

export type RelabelPreviewScope = 'image' | 'video'
export type RelabelPreviewState = 'idle' | 'loading' | 'ready' | 'error'

export interface UseRelabelPreview {
  preview: ShallowRef<RelabelPreview | null>
  state: Ref<RelabelPreviewState>
  loading: Readonly<Ref<boolean>>
  error: ShallowRef<unknown>
  refresh: () => Promise<RelabelPreview | null>
  reset: () => void
}

/** Read-only relabel cost estimate for the selected account/workspace and one creative scope. */
export function useRelabelPreview(
  scope: MaybeRefOrGetter<RelabelPreviewScope | null | undefined>,
): UseRelabelPreview {
  const creditStore = useCreditStore()
  const { workspaceId } = useWorkspace()
  const preview = shallowRef<RelabelPreview | null>(null)
  const error = shallowRef<unknown>(null)
  const state = ref<RelabelPreviewState>('idle')
  let requestGeneration = 0

  const loading = computed(() => state.value === 'loading')

  function reset(): void {
    requestGeneration += 1
    preview.value = null
    error.value = null
    state.value = 'idle'
  }

  async function refresh(): Promise<RelabelPreview | null> {
    const activeScope = toValue(scope)
    const numericWorkspaceId = Number(workspaceId.value)
    if (!activeScope || !Number.isFinite(numericWorkspaceId) || numericWorkspaceId <= 0) {
      reset()
      return null
    }

    const generation = ++requestGeneration
    preview.value = null
    error.value = null
    state.value = 'loading'

    try {
      const result = await creditStore.previewRelabel({
        scope: activeScope,
        workspace_id: numericWorkspaceId,
      })
      if (generation !== requestGeneration) return null
      preview.value = result
      state.value = result ? 'ready' : 'idle'
      return result
    } catch (cause: unknown) {
      if (generation !== requestGeneration) return null
      error.value = cause
      state.value = 'error'
      return null
    }
  }

  watch(
    () => [toValue(scope), workspaceId.value] as const,
    ([nextScope, nextWorkspace], [previousScope, previousWorkspace]) => {
      if (nextScope !== previousScope || nextWorkspace !== previousWorkspace) reset()
    },
  )
  onScopeDispose(() => {
    requestGeneration += 1
  })

  return { preview, state, loading, error, refresh, reset }
}
